import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Activity, 
  Users, 
  Settings, 
  Menu,
  X,
  LogOut,
  User,
  ChevronDown,
  Zap,
  Shield,
  Clock,
  PieChart,
  Calendar
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import VersionDisplay from './VersionDisplay';
import RouteErrorBoundary from './RouteErrorBoundary';

// Admin Ribbon Component
const AdminRibbon = () => (
  <div className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 ml-2">
    <Shield className="w-3 h-3 mr-1" />
    Admin
  </div>
);

const Layout = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: Activity,
      description: 'Analytics Dashboard'
    },
    {
      name: 'AI Models',
      path: '/ai-model-distribution',
      icon: PieChart,
      description: 'Model distribution'
    },
    {
      name: 'Teams',
      path: '/teams',
      icon: Users,
      description: 'Team performance'
    },
    {
      name: 'Developers',
      path: '/users',
      icon: User,
      description: 'Dev analytics'
    },
    {
      name: 'Monthly Usage',
      path: '/monthly-usage',
      icon: Calendar,
      description: 'Billing group spend'
    },

    // Admin only features
    ...(user?.role === 'admin' ? [
      {
        name: 'Data Import',
        path: '/data-import',
        icon: Zap,
        description: 'Cursor API integration',
        isAdminOnly: true
      },
      {
        name: 'Scheduler',
        path: '/scheduler',
        icon: Clock,
        description: 'Automated imports',
        isAdminOnly: true
      },
      {
        name: 'Admin',
        path: '/admin',
        icon: Settings,
        description: 'System administration',
        isAdminOnly: true
      }
    ] : [])
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="h-screen bg-secondary-50 flex overflow-hidden">
      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-strong transform transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo and brand */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-secondary-200 flex-shrink-0">
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-primary-600 mr-3" />
              <span className="text-xl font-bold text-secondary-900">
                Cursor Analytics
              </span>
            </div>
            <button
              onClick={closeSidebar}
              className="lg:hidden p-1 rounded-lg hover:bg-secondary-100 transition-colors"
            >
              <X className="w-5 h-5 text-secondary-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  closeSidebar();
                }}
                className={`
                  nav-link w-full text-left ${isActivePath(item.path) ? 'active' : ''}
                `}
              >
                <item.icon className="w-5 h-5 mr-3" />
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-medium">{item.name}</span>
                    {item.isAdminOnly && <AdminRibbon />}
                  </div>
                  <div className="text-xs text-secondary-500">{item.description}</div>
                </div>
              </button>
            ))}
          </nav>

          {/* User info */}
          <div className="border-t border-secondary-200 p-4 flex-shrink-0">
            <div className="flex items-center text-sm">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                <User className="w-4 h-4 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-secondary-900 truncate">
                  {user?.email}
                </p>
                <p className="text-xs text-secondary-500 capitalize">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-secondary-200 flex-shrink-0 z-30">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-secondary-100 transition-colors mr-2"
              >
                <Menu className="w-5 h-5 text-secondary-600" />
              </button>
              
              <h1 className="text-lg font-semibold text-secondary-900 capitalize">
                {location.pathname.split('/')[1] || 'Dashboard'}
              </h1>
            </div>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-secondary-50 transition-colors"
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <span className="hidden sm:block text-sm font-medium text-secondary-900">
                  {user?.email}
                </span>
                <ChevronDown className="w-4 h-4 text-secondary-500" />
              </button>

              {/* User dropdown */}
              {userMenuOpen && (
                <div className="dropdown">
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-secondary-200">
                      <p className="text-sm font-medium text-secondary-900">
                        {user?.email}
                      </p>
                      <p className="text-xs text-secondary-500 capitalize">
                        {user?.role} Account
                      </p>
                    </div>
                    
                    <button
                      onClick={() => {
                        setChangePasswordOpen(true);
                        setUserMenuOpen(false);
                      }}
                      className="dropdown-item w-full text-left flex items-center text-secondary-700 hover:bg-secondary-50"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Change Password
                    </button>
                    
                    <button
                      onClick={handleLogout}
                      className="dropdown-item w-full text-left flex items-center text-danger-600 hover:bg-danger-50"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content - Scrollable */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-6 py-6">
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>

        {/* Change Password Modal */}
        <ChangePasswordModal 
          isOpen={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />
      </div>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div 
          className="fixed inset-0 z-20"
          onClick={() => setUserMenuOpen(false)}
        />
      )}

      {/* Version Display */}
      <VersionDisplay />
    </div>
  );
};

// Change Password Modal Component
const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setError('');
    setSuccess(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <h3 className="text-lg font-semibold text-secondary-900">Change Password</h3>
          <button
            onClick={() => {
              onClose();
              resetForm();
            }}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {success && (
            <div className="mb-4 p-3 bg-success-50 border border-success-200 text-success-800 rounded-md">
              Password changed successfully!
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 text-danger-800 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-secondary-500 mt-1">
                Must be at least 6 characters long
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                onClose();
                resetForm();
              }}
              className="flex-1 px-4 py-2 text-sm font-medium text-secondary-700 bg-secondary-100 hover:bg-secondary-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Layout; 